import React, { useState, useEffect, useMemo } from 'react';
import Grid from '@mui/material/Grid2';
import { TypographyStepTitle } from '../style/typography';
import { Box, Dialog, IconButton, Tooltip, Table, TableBody, TableContainer, TableHead, TableRow, Paper, Chip } from '@mui/material';
import { Edit, Fullscreen, FullscreenExit } from '@mui/icons-material';
import { RequestDetailButton } from "../style/button";
import CommonCard from './common_card';
import { SmallCell } from '../style/tablecell';
import { TypographyNomalBold } from '../style/typography';

const getStatusInfo = (lastTask, nextTask) => {
  if (lastTask === '報告対象外') return { label: '報告対象外', bgColor: '#cccccc', textColor: '#666', isFin: true };
  if (lastTask === '業務完了') return { label: '業務完了', bgColor: 'rgb(72,161,75)', textColor: '#fff', isFin: true };
  if (!lastTask || lastTask === '-') return { label: '未対応', bgColor: '#ed6c02', textColor: '#fff', isFin: false }; // warning相当の色
  return { label: '対応中', bgColor: 'rgb(53,121,188)', textColor: '#fff', isFin: false };
};

const InnerTable = ({ items, isFullscreenMode }) => (
  <Box sx={{
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden'
  }}>
    <TableContainer
      component={Paper}
      sx={{
        // 以前修正した高さのロジック
        height: isFullscreenMode ? 'calc(100vh - 48px)' : '420px',
        overflowY: 'auto',
        boxShadow: 'none',
      }}
    >
      <Table stickyHeader sx={{ tableLayout: 'auto', minWidth: '100%' }}>
        <TableHead>
          <TableRow>
            <SmallCell sx={{ backgroundColor: '#fff' }}><TypographyNomalBold>施設名</TypographyNomalBold></SmallCell>
            <SmallCell sx={{ backgroundColor: '#fff' }}><TypographyNomalBold>対応状況</TypographyNomalBold></SmallCell>
            <SmallCell sx={{ backgroundColor: '#fff' }}><TypographyNomalBold>対応中業務</TypographyNomalBold></SmallCell>
            <SmallCell sx={{ backgroundColor: '#fff' }}><TypographyNomalBold>対応済み業務</TypographyNomalBold></SmallCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => {
            const status = getStatusInfo(item.latest_completed_task_name, item.status_next);
            const isComplete = status.label === '業務完了';
            const isNotReport = status.label === '報告対象外';
            return (
              <TableRow
                key={item.key}
                sx={{ backgroundColor: isNotReport ? '#eeeeee' : isComplete ? '#e8f5e9' : 'transparent' }}
              >
                <SmallCell>{item.facility_name}</SmallCell>
                <SmallCell> 
                  <Chip
                    label={status.label}
                    size="small"
                    sx={{
                      fontWeight: 'bold',
                      width: '100px',
                      backgroundColor: status.bgColor,
                      color: status.textColor
                    }}
                  />
                </SmallCell>
                <SmallCell>{status.isFin ? '-': item.status_next}</SmallCell>
                <SmallCell>{status.isFin ? '-': item.latest_completed_task_name}</SmallCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
);

const PowerplantCompatibilityStatus = ({ title, onReportClick, type, reportDetailList, isButtonDisabled }) => {
  const [selectedItem, setSelectedItem] = useState("");
  const [isFullscreen, setIsFullscreen] = useState(false);

  const items = useMemo(() => {
    if (Array.isArray(reportDetailList) && reportDetailList.length) {
      return reportDetailList.map((r, i) => ({
        key: r.uuid ?? `r-${i}`,
        facility_name: r.facility_name ?? '-',
        status_next: r.status_next ?? '-',
        latest_completed_task_name: r.latest_completed_task_name ?? '-',
      }));
    }
    return [];
  }, [reportDetailList]);

  useEffect(() => {
    if (!selectedItem && items.length) {
      setSelectedItem(items[0].key);
    }
  }, [items, selectedItem]);

  const handleFullscreenToggle = () => {
    setIsFullscreen(!isFullscreen);
  };

  return (
    <>
      <Grid size={{ xs: 12, sm: 12, md: 6 }}>
        <Grid container size={{ xs: 12 }}>
          <Grid size={{ xs: 7 }} display="flex" alignItems="center">
            <TypographyStepTitle sx={{ fontSize: { xs: "1.4rem", sm: "1.5rem", md: "1.5rem" } }}>
              {title}
            </TypographyStepTitle>
            <Tooltip title="全画面表示">
              <IconButton
                onClick={handleFullscreenToggle}
                sx={{
                  ml: 1,
                  color: 'rgba(10, 124, 183, 1.0)',
                  '&:hover': {
                    backgroundColor: 'rgba(10, 124, 183, 0.1)',
                  }
                }}
              >
                <Fullscreen />
              </IconButton>
            </Tooltip>
          </Grid>

          <Grid size={{ xs: 5 }} display="flex" justifyContent="flex-end">
            {!isButtonDisabled && (
              <RequestDetailButton
                size="large"
                startIcon={<Edit />}
                onClick={() => onReportClick(reportDetailList, type)}
              >
                報告作業を開始する
              </RequestDetailButton>
            )}
          </Grid>
        </Grid>

        <CommonCard
          xsHeight={500}
          height={435}
          child={<InnerTable items={items} isFullscreenMode={false} />}
        />
      </Grid>

      {/* 全画面表示ダイアログ */}
      <Dialog
        fullScreen
        open={isFullscreen}
        onClose={handleFullscreenToggle}
        sx={{
          '& .MuiDialog-paper': {
            backgroundColor: '#f5f5f5',
          }
        }}
      >
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            padding: 3,
            boxSizing: 'border-box'
          }}
        >
          <Box
            sx={{
              backgroundColor: '#fff',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              position: 'relative'
            }}
          >
            <Tooltip title="全画面表示を終了">

              <IconButton
                onClick={handleFullscreenToggle}
                sx={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  zIndex: 900,
                  color: 'rgba(10, 124, 183, 1.0)',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  '&:hover': {
                    backgroundColor: 'rgba(10, 124, 183, 0.1)',
                  }
                }}
              >
                <FullscreenExit fontSize="large" />
              </IconButton>
            </Tooltip>
            <InnerTable items={items} isFullscreenMode={true} />
          </Box>
        </Box>
      </Dialog>
    </>
  );
};

export default PowerplantCompatibilityStatus;